import React, { useEffect, useId, useState } from 'react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { LEGAL_DOCUMENTS } from '../Legal/legalDocuments';
import { createDraft, useAdminLegalDocuments } from '../../agents/legalDocumentsAgent';
import LegalDocumentEditor from './LegalDocumentEditor';
import {
  acceptanceLabel,
  formatShortDate,
  groupVersions,
  suggestNextVersion,
} from './legalDocumentsUtils';
import styles from './LegalDocumentsManager.module.css';

const DOCS = [LEGAL_DOCUMENTS.privacy, LEGAL_DOCUMENTS.terms];

/**
 * NewDraftDialog — asks for the new version label, then creates the draft.
 * The server copies title and text from the live version.
 */
function NewDraftDialog({ document: legalDoc, suggestion, hasLive, onCreated, onCancel }) {
  const [version, setVersion] = useState(suggestion);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const formId = useId();

  useEffect(() => {
    setVersion(suggestion);
  }, [suggestion]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const label = version.trim();
    if (!label) {
      setError('Enter a version label.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { doc_key: legalDoc.key, version: label };
      if (summary.trim()) payload.change_summary = summary.trim();
      const draft = await createDraft(payload);
      onCreated(draft);
    } catch (err) {
      setError(err?.message || 'Failed to create the draft');
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : onCancel}
      title={`New ${legalDoc.title} draft`}
      size="sm"
      footer={(
        <div className={styles.dialogActions}>
          <Button variant="ghost" className={styles.btn} onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" className={styles.btn} type="submit" form={formId} disabled={busy}>
            {busy ? 'Creating…' : 'Create draft'}
          </Button>
        </div>
      )}
    >
      <form id={formId} className={styles.dialogBody} onSubmit={handleSubmit} noValidate>
        <p>
          {hasLive
            ? `The title and text start as a copy of the live version, so you only edit what changes.`
            : `Nothing is published yet, so the draft starts empty.`}
        </p>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${formId}-version`}>Version</label>
          <input
            id={`${formId}-version`}
            className={styles.input}
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            maxLength={50}
            required
            aria-describedby={`${formId}-version-help`}
          />
          <span id={`${formId}-version-help`} className={styles.help}>
            This is the version people accept at sign-up once it&rsquo;s published.
          </span>
        </div>
        <label className={styles.field} htmlFor={`${formId}-summary`}>
          <span className={styles.label}>Change summary <span className={styles.optional}>(optional)</span></span>
          <textarea
            id={`${formId}-summary`}
            className={`${styles.input} ${styles.summaryInput}`}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={2}
          />
        </label>
        {error && <p className={styles.inlineError} role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

/**
 * One document's card: live version, open draft, version history.
 */
function DocumentCard({ document: legalDoc, group, onOpen, onNewDraft }) {
  const { current, draft, published } = group;
  const headingId = useId();

  return (
    <section className={styles.docCard} aria-labelledby={headingId} data-testid={`legal-doc-${legalDoc.key}`}>
      <header className={styles.docHeader}>
        <h2 id={headingId} className={styles.docTitle}>{legalDoc.title}</h2>
        <a href={legalDoc.path} target="_blank" rel="noopener noreferrer" className={styles.pathLink}>
          {legalDoc.path}
          <span className={styles.visuallyHidden}> (opens in a new tab)</span>
        </a>
      </header>

      <div className={styles.liveBlock}>
        <p className={styles.blockLabel}>Live</p>
        {current ? (
          <>
            <p className={styles.blockVersion}>
              <span className={styles.versionTag}>{current.version}</span>
              <span className={`${styles.approvalPill} ${current.counsel_approved ? styles.approvalYes : styles.approvalNo}`}>
                {current.counsel_approved ? 'Counsel approved' : 'Not counsel approved'}
              </span>
            </p>
            <dl className={styles.facts}>
              <div>
                <dt>Published</dt>
                <dd>
                  {formatShortDate(current.published_at)}
                  {current.published_by_email ? ` by ${current.published_by_email}` : ''}
                </dd>
              </div>
              <div>
                <dt>Accepted by</dt>
                <dd>{acceptanceLabel(current.acceptance_count)}</dd>
              </div>
            </dl>
            <Button
              variant="ghost"
              size="sm"
              className={styles.btn}
              onClick={() => onOpen(legalDoc, current.id)}
              aria-label={`View ${legalDoc.title} ${current.version}`}
            >
              View
            </Button>
          </>
        ) : (
          <p className={styles.emptyText}>
            Nothing published yet — {legalDoc.path} tells visitors it isn&rsquo;t available.
          </p>
        )}
      </div>

      <div className={`${styles.draftBlock} ${draft ? styles.draftBlockOpen : ''}`}>
        <p className={styles.blockLabel}>Draft</p>
        {draft ? (
          <>
            <p className={styles.blockVersion}>
              <span className={styles.versionTag}>{draft.version}</span>
              <span className={styles.mutedText}>
                Last saved {formatShortDate(draft.updated_at || draft.created_at)}
                {draft.created_by_email ? ` · started by ${draft.created_by_email}` : ''}
              </span>
            </p>
            {draft.change_summary && <p className={styles.summaryText}>{draft.change_summary}</p>}
            <Button
              variant="primary"
              size="sm"
              className={styles.btn}
              onClick={() => onOpen(legalDoc, draft.id)}
              aria-label={`Continue editing ${legalDoc.title} draft ${draft.version}`}
            >
              Continue editing
            </Button>
          </>
        ) : (
          <>
            <p className={styles.emptyText}>No draft in progress.</p>
            <Button
              variant="secondary"
              size="sm"
              className={styles.btn}
              onClick={() => onNewDraft(legalDoc)}
              aria-label={`New ${legalDoc.title} draft`}
            >
              New draft
            </Button>
          </>
        )}
      </div>

      <details className={styles.history}>
        <summary className={styles.historySummary}>
          Version history <span className={styles.mutedText}>({published.length} published)</span>
        </summary>
        {published.length === 0 ? (
          <p className={styles.emptyText}>No published versions yet.</p>
        ) : (
          <ol className={styles.historyList}>
            {published.map((v) => (
              <li key={v.id} className={styles.historyItem}>
                <span className={styles.historyVersion}>
                  {v.version}
                  {v.is_current && <span className={styles.liveDot}>Live</span>}
                </span>
                <span className={styles.mutedText}>{formatShortDate(v.published_at)}</span>
                <span className={styles.mutedText}>{acceptanceLabel(v.acceptance_count)}</span>
                <span className={styles.mutedText}>{v.counsel_approved ? 'Approved' : 'Unapproved'}</span>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => onOpen(legalDoc, v.id)}
                  aria-label={`View ${legalDoc.title} ${v.version}`}
                >
                  View
                </button>
              </li>
            ))}
          </ol>
        )}
      </details>
    </section>
  );
}

/**
 * LegalDocumentsManager — Admin → Legal documents. Drafts, edits, and
 * publishes new versions of the Privacy Notice and Terms of Service that
 * /privacy, /terms and the sign-up disclaimer serve.
 */
export default function LegalDocumentsManager() {
  const { versions, loading, error, isAdmin, refresh } = useAdminLegalDocuments();
  const [open, setOpen] = useState(null); // { doc, id }
  const [newDraftFor, setNewDraftFor] = useState(null);
  const [flash, setFlash] = useState(null);

  const openVersion = (doc, id) => {
    setFlash(null);
    setOpen({ doc, id });
  };

  const backToOverview = (message = null) => {
    setOpen(null);
    setFlash(message);
    refresh();
  };

  if (loading) {
    return (
      <div className={styles.shell}>
        <p className={styles.loading} role="status">Loading legal documents…</p>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className={styles.shell}>
        <div className={styles.forbidden}>
          <h2>Access denied</h2>
          <p>You do not have permission to manage legal documents.</p>
        </div>
      </div>
    );
  }

  if (open) {
    return (
      <div className={styles.shell}>
        <LegalDocumentEditor
          key={open.id}
          versionId={open.id}
          document={open.doc}
          onBack={() => backToOverview()}
          onChanged={refresh}
          onPublished={(summary) => backToOverview(
            `${open.doc.title} ${summary?.version || ''} is live on ${open.doc.path}.`.replace(/\s+/g, ' '),
          )}
          onDeleted={(version) => backToOverview(`Deleted draft ${version || ''}.`.replace(/\s+\./, '.'))}
        />
      </div>
    );
  }

  const newDraftGroup = newDraftFor ? groupVersions(versions, newDraftFor.key) : null;

  return (
    <div className={styles.shell}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Legal documents</h1>
        <p className={styles.pageSubtitle}>
          The Privacy Notice and Terms of Service shown on /privacy and /terms and accepted at sign-up.
          Edit a draft, get counsel sign-off, then publish — published versions are frozen so every
          acceptance points at the exact text the person saw.
        </p>
      </header>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <span>{error}</span>
          <Button variant="secondary" size="sm" className={styles.btn} onClick={refresh}>Retry</Button>
        </div>
      )}

      {flash && (
        <p className={styles.flash} role="status">{flash}</p>
      )}

      <div className={styles.docGrid}>
        {DOCS.map((doc) => (
          <DocumentCard
            key={doc.key}
            document={doc}
            group={groupVersions(versions, doc.key)}
            onOpen={openVersion}
            onNewDraft={(d) => {
              setFlash(null);
              setNewDraftFor(d);
            }}
          />
        ))}
      </div>

      {newDraftFor && (
        <NewDraftDialog
          document={newDraftFor}
          suggestion={suggestNextVersion(newDraftGroup.all)}
          hasLive={Boolean(newDraftGroup.current)}
          onCancel={() => setNewDraftFor(null)}
          onCreated={(draft) => {
            const doc = newDraftFor;
            setNewDraftFor(null);
            refresh();
            if (draft && draft.id != null) {
              setOpen({ doc, id: draft.id });
            }
          }}
        />
      )}
    </div>
  );
}
